import { TranslateRegistry } from '../../../../services/translate/translate.registry';

import { SEARCH_HINTS } from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import { SearchType } from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import type { FieldOperationsListItem } from './field-operations.types';
import { DatepickerRange, RangeDatepickerComponent } from '@ui/aos-lib';
import { DateUtil } from '@portal/shared/utils/date-util';
import { getUrlPathForDeviceTypeName } from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.helpers';
import { DeviceType } from '../../devices/components/devices.models';
import { CheckmarkColumnComponent } from '../../../../shared/components/listing-table/components/checkmark-column';
import { MultiLineHighlightComponent } from '@portal/shared/components/listing-table/components/multi-line-highlight-column.component';
import {
  CustomizableColumnComponent,
  CustomizableColumnType,
  CustomizableTableColumnValue
} from '@portal/shared/components/listing-table/components/customizable-column.component';

const FILTER_WIDTH = 160;

export const enum FieldOperationSortFieldName {
  InstallationDateTimeUtc = 'InstallationDateTimeUtc',
  InstalledBy = 'InstalledBy',
  WakeupDateTimeUtc = 'WakeupDateTimeUtc',
  InitialReadDateTimeUtc = 'InitialReadDateTimeUtc'
}

export const enum FieldOperationSearchFieldName {
  InstalledBy = 'InstalledBy',
  MtuDeviceId = 'CommDeviceId',
  MeterSerialNumber = 'SerialNumber',
  MeterModel = 'Model',
  ServiceLocationIds = 'ServiceLocationIds'
}

export const enum FieldOperationFilterName {
  DeviceType = 'DeviceType',
  ReportGroupUID = 'ReportGroupUID',
  InstallationDateTimeUtc = 'InstallationDateTimeUtc',
  InstallationReason = 'InstallationReason',
  WakeUpReceived = 'WakeUpReceived',
  ReadingReceived = 'ReadingReceived'
}

export const fieldOperationsTableSortFields: Record<string, FieldOperationSortFieldName> = {
  installationDateTime: FieldOperationSortFieldName.InstallationDateTimeUtc,
  installedBy: FieldOperationSortFieldName.InstalledBy,
  wakeupDateTime: FieldOperationSortFieldName.WakeupDateTimeUtc,
  initialReadDateTime: FieldOperationSortFieldName.InitialReadDateTimeUtc
};

export const FIELD_OPERATIONS_SEARCH_TYPES: SearchType[] = [
  {
    text: TranslateRegistry.get.SEARCH.TYPE.COMM_DEVICE_ID,
    value: 1,
    types: [FieldOperationSearchFieldName.MtuDeviceId],
    hintText: SEARCH_HINTS.CommDeviceId
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.INSTALLED_BY,
    value: 2,
    types: [FieldOperationSearchFieldName.InstalledBy],
    hintText: SEARCH_HINTS.InstalledBy
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.METER_SERIAL_NUMBER,
    value: 3,
    types: [FieldOperationSearchFieldName.MeterSerialNumber],
    hintText: SEARCH_HINTS.MeterSerialNumber
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.METER_MODEL,
    value: 4,
    types: [FieldOperationSearchFieldName.MeterModel],
    hintText: SEARCH_HINTS.MeterModel
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.SERVICE_LOCATION_NUMBER,
    value: 5,
    types: [FieldOperationSearchFieldName.ServiceLocationIds],
    hintText: SEARCH_HINTS.ServiceLocationNumber
  }
];

export const INSTALLATION_DEFAULT_FILTER = {
  filterType: FieldOperationFilterName.DeviceType,
  filteredValues: [DeviceType.MTU]
};

export const REPORT_GROUP = {
  title: TranslateRegistry.get.FILTER.TITLE.REPORT_GROUP,
  items: [],
  filterType: FieldOperationFilterName.ReportGroupUID,
  selected: [],
  isMultiselect: true,
  width: FILTER_WIDTH
};

export const FIELD_OPERATIONS_DATE_TIME = {
  title: TranslateRegistry.get.FILTER.TITLE.DATE_TIME,
  items: [],
  selected: [],
  filterType: FieldOperationFilterName.InstallationDateTimeUtc,
  isDatepicker: true,
  minDate: null,
  width: 0,
  dateRange: (): string[] => [
    DateUtil.toISOString((RangeDatepickerComponent.LAST_MONTH.getValue() as DatepickerRange).start),
    DateUtil.toISOString((RangeDatepickerComponent.LAST_MONTH.getValue() as DatepickerRange).end)
  ],
  defaultDateRange: (): string[] => [
    DateUtil.toISOString((RangeDatepickerComponent.LAST_MONTH.getValue() as DatepickerRange).start),
    DateUtil.toISOString((RangeDatepickerComponent.LAST_MONTH.getValue() as DatepickerRange).end)
  ]
};

const INSTALLATION_REASON_ITEMS = [
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.ADD_METER, value: 'AddMeter' },
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.ADD_MTU, value: 'AddMTU' },
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.ADD_MTU_ADD_METER, value: 'AddMTUAddMeter' },
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.READ_MTU, value: 'ReadMTU' },
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.REPLACE_METER, value: 'ReplaceMeter' },
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.REPLACE_MTU, value: 'ReplaceMTU' },
  {
    text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.REPLACE_MTU_REPLACE_METER,
    value: 'ReplaceMTUReplaceMeter'
  },
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.TURN_OFF_MTU, value: 'TurnOffMTU' },
  { text: TranslateRegistry.get.FILTER.COMMON.UNKNOWN, value: 'Unknown' },
  { text: TranslateRegistry.get.FILTER.INSTALLATION_REASONS.VALVE_POSITION, value: 'ValvePosition' }
];

export const REASON = {
  title: TranslateRegistry.get.FILTER.TITLE.REASON,
  items: INSTALLATION_REASON_ITEMS,
  filterType: FieldOperationFilterName.InstallationReason,
  selected: INSTALLATION_REASON_ITEMS,
  isMultiselect: true,
  width: 184
};

export const WAKE_UP_RECEIVED = {
  title: TranslateRegistry.get.FILTER.TITLE.WAKE_UP_RECEIVED,
  items: [
    { text: 'Yes', value: 'true' },
    { text: 'No', value: 'false' }
  ],
  filterType: FieldOperationFilterName.WakeUpReceived,
  selected: [
    { text: 'Yes', value: 'true' },
    { text: 'No', value: 'false' }
  ],
  isMultiselect: true,
  isNonSortable: true,
  width: FILTER_WIDTH
};

export const READING_RECEIVED = {
  title: TranslateRegistry.get.FILTER.TITLE.READING_RECEIVED,
  items: [
    { text: 'Yes', value: 'true' },
    { text: 'No', value: 'false' }
  ],
  filterType: FieldOperationFilterName.ReadingReceived,
  selected: [
    { text: 'Yes', value: 'true' },
    { text: 'No', value: 'false' }
  ],
  isMultiselect: true,
  isNonSortable: true,
  width: FILTER_WIDTH
};

export const TABLE_COLUMN = [
  {
    field: 'installationDateTime',
    sortable: true,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.OPERATION_DATE_TIME
  },
  {
    field: 'mtuDeviceLabel',
    link: {
      getLink: (dataItem: FieldOperationsListItem): string =>
        `devices/${getUrlPathForDeviceTypeName(DeviceType.MTU)}/${dataItem.deviceUID}`
    },
    sortable: false,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.COMM_DEVICE_ID
  },
  {
    field: 'installationReason',
    sortable: false,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.REASON
  },
  {
    field: 'meterSerialNumber',
    sortable: false,
    width: 100,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.METER_SERIAL_NUMBER,
    cmp: CustomizableColumnComponent,
    options: {
      columnType: CustomizableColumnType.LINK,
      columnValues: (tableItem: FieldOperationsListItem): CustomizableTableColumnValue[] =>
        tableItem.meterSerialNumber.map(meter => ({
          text: meter.serialNumber ?? '',
          routerLink: ['/devices/metering', meter.deviceUID ?? '']
        }))
    }
  },
  {
    field: 'meterModel',
    sortable: false,
    width: 250,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.METER_MODEL,
    cmp: MultiLineHighlightComponent
  },
  {
    field: 'port',
    sortable: false,
    width: 100,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.PORT,
    cmp: MultiLineHighlightComponent
  },
  {
    field: 'serviceLocationIds',
    sortable: false,
    width: 150,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.SERVICE_LOCATION_NUMBER,
    cmp: MultiLineHighlightComponent
  },
  {
    field: 'wakeupDateTime',
    sortable: false,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.WAKE_UP_RECEIVED,
    cmp: CheckmarkColumnComponent
  },
  {
    field: 'initialReadDateTime',
    sortable: false,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.READING_RECEIVED,
    cmp: CheckmarkColumnComponent
  },
  {
    field: 'installedBy',
    sortable: true,
    title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.INSTALLED_BY
  }
];
